<template>
  <material-card class="mx-2">
    <v-layout px-3 slot="header">
      <v-flex mt-2 pl-3 align-center justify-center>
        <div class="headline">Usługi</div>

        <p>Lista usług</p>
      </v-flex>
    </v-layout>

    <v-layout>
      <v-flex>
        <v-tabs v-model="activeTab">
          <v-tab v-for="(tab, index) in tabs" :key="index" exact :to="route(tab)">{{ tab.text }}</v-tab>
        </v-tabs>
      </v-flex>
    </v-layout>

    <v-layout text-xs-right>
      <v-flex>
        <check-permission permissions="SHARED_BENEFIT.ADD">
          <v-btn @click="showAddModal">Dodaj usługę współdzieloną</v-btn>
        </check-permission>

        <check-permission permissions="BENEFIT.CSV">
          <post-form text="Pobierz CSV" :url="csvUrl" :fields="csvFilters" />
        </check-permission>
      </v-flex>
    </v-layout>

    <material-table
      entity-type="benefit"
      :headers="table.headers"
      :actions="actions"
      :items="list"
      :total-count="count"
      @confirm="showConfirmDialog($event.id)"
      @delete="showDeleteDialog($event.id)"
      @details="goToDetails"
    />

    <material-dialog
      :title="confirm.title"
      :text="confirm.text"
      :show="confirm.show"
      @close="hideDialog"
      @confirm="confirmAction"
    />

    <benefit-modal
      :show="showModal"
      :id="idToEdit"
      :edit="!!idToEdit"
      :shared="true"
      @close="hideModal"
      @save="add"
      @update="update"
    />
  </material-card>
</template>

<script>
import { benefitType, StateString } from '@witkac/kartamiejska-shared';
import { mapState } from 'vuex';

import MaterialCard from '@/components/material/Card';
import MaterialDialog from '@/components/material/Dialog';
import MaterialTable from '@/components/material/Table';
import PostForm from '@/components/postForm/PostForm';
import { BenefitChangeState, BenefitChangeStateTranslation } from '@/enums/benefitChangeState';
import { BenefitTabs, BenefitTabsTranslation } from '@/enums/benefitTabs.enum';
import { listMixin } from '@/mixins/list';
import { CONFIRM_ACTION, DELETE_ACTION } from '@/utils/actions';
import { benefitTabFilters } from '@/utils/benefitTabFilters';
import { config } from '@/utils/config';
import BenefitModal from '@/views/partners/tabs/modals/AddBenefit.modal';

// @vuese
// Lista usług
// @group Benefit
export default {
  mixins: [
    listMixin('benefit', {
      added: 'Pomyślnie dodano usługę.',
      listError: 'Wystąpił błąd podczas pobierania listy usług.',
      getError: 'Wystąpił błąd podczas pobierania danych usługi.',
      acceptSuccess: 'Pomyślnie zaakceptowano usługę',
      deleted: 'Usunięto usługę.',
      deleteError: 'Nie udało się usunąć usługi.'
    })
  ],
  components: {
    MaterialCard,
    MaterialDialog,
    MaterialTable,
    PostForm,
    BenefitModal
  },
  data() {
    return {
      activeTab: this.$route.path,
      csvUrl: config.csvURL + '/benefit',
      table: {
        headers: [
          { text: 'ID', orderBy: 'id', value: 'id' },
          {
            text: 'Nazwa usługi',
            link: true,
            value: 'name',
            orderBy: 'name'
          },
          {
            text: 'Partner/Partnerzy',
            value: 'partner',
            sortable: false,
            render: item => {
              if (item.partner && item.partner.name) {
                return item.partner.name;
              }
              if (!item.partners || !item.partners.length) {
                return '';
              }
              const firstThree = item.partners.map(partner => partner.name).slice(0, 3);
              const left = item.partners.length - 3;
              return `${firstThree.join(', ')} ${left > 0 ? 'oraz ' + left + ' więcej' : ''}`;
            }
          }
        ]
      },
      confirm: { text: '', title: '', show: false, id: undefined, type: '' }
    };
  },
  methods: {
    // @vuese
    // Akcja po potwierdzeniu
    confirmAction() {
      if (this.confirm.type === 'DELETE') {
        this.remove();
      } else {
        this.confirmBenefit();
      }
    },
    // @vuese
    // Potwierdzenie Usługi
    confirmBenefit() {
      this.$store
        .dispatch('benefit/confirm', this.confirm.id)
        .then(() => {
          this.$store.dispatch('notifySuccess', { title: 'Pomyślnie zaakceptowano usługę' });
          this.reloadList();
        })
        .catch(error => {
          this.$store.dispatch('notifyError', { title: 'Nie udało się zaakceptować usługi' });
        });
    },
    // @vuese
    // Otwiera okienko z potwierdzeniem usunięcia
    // @arg `id` - identyfikator usługi
    showDeleteDialog(id) {
      this.confirm.title = 'Potwierdź usunięcie';
      this.confirm.text = 'Czy na pewno chcesz usunąć usługę?';
      this.confirm.type = 'DELETE';
      this.confirm.id = id;
      this.confirm.show = true;
    },
    // @vuese
    // Otwiera okienko z potwierdzeniem usługi
    // @arg `id` - identyfikator usługi
    showConfirmDialog(id) {
      this.confirm.title = 'Potwierdź akceptację';
      this.confirm.text = 'Czy na pewno chcesz zaakceptować usługę?';
      this.confirm.type = 'CONFIRM';
      this.confirm.id = id;
      this.confirm.show = true;
    },
    // @vuese
    // Ukrywa okienko z potwierdzeniem
    hideDialog() {
      this.confirm.show = false;
    },
    // @vuese
    // Usuwa usługę
    remove() {
      this.$store
        .dispatch('benefit/remove', this.confirm.id)
        .then(() => {
          this.notifySuccess('Usunięto usługę.');
          this.reloadList();
        })
        .catch(err => this.notifyError('Nie udało się usunąć usługi.'));
    },
    benefitType,
    // @vuese
    // Tworzy filtr na podstawie paginacji
    filters(pagination, route) {
      const filters = benefitTabFilters(route, this.$store);
      return { ...pagination, ...filters };
    },
    // @vuese
    // Przenosi do szczegółów
    goToDetails(benefit) {
      if ((this.benefitConfirmSetting && benefit.state === StateString.NOT_CONFIRMED) || benefit.shared) {
        this.$router.push({ name: 'benefitDetails', params: { benefitId: benefit.id } });
      } else {
        this.$router.push({ name: 'partnerBenefits', params: { id: benefit.partner.id } });
      }
    },

    // @vuese
    // Routing zakładki
    route(tab) {
      return { ...tab.route, query: { ...this.$route.query, page: 1 } };
    }
  },
  computed: {
    ...mapState({
      list: state => state.benefit.list,
      benefitConfirmSetting: state => JSON.parse(state.settings.settings.BENEFIT_CONFIRM.value)
    }),

    actions() {
      if (
        this.benefitConfirmSetting &&
        this.$route.params.state &&
        this.tabs.find(item => item.route.params.state === this.$route.params.state).actions
      ) {
        return [DELETE_ACTION, CONFIRM_ACTION];
      }
    },
    tabs() {
      const tabs = [
        {
          text: BenefitTabsTranslation[BenefitTabs.ACTIVE],
          route: { name: 'benefitList', params: { state: BenefitTabs.ACTIVE } }
        },
        {
          text: BenefitTabsTranslation[BenefitTabs.INACTIVE],
          route: { name: 'benefitList', params: { state: BenefitTabs.INACTIVE } }
        },
        {
          text: BenefitTabsTranslation[BenefitTabs.UPCOMING],
          route: { name: 'benefitList', params: { state: BenefitTabs.UPCOMING } }
        },
        {
          text: BenefitTabsTranslation[BenefitTabs.PAST],
          route: { name: 'benefitList', params: { state: BenefitTabs.PAST } }
        },
        {
          text: BenefitTabsTranslation[BenefitTabs.HIDDEN],
          route: { name: 'benefitList', params: { state: BenefitTabs.HIDDEN } }
        }
      ];
      if (!this.benefitConfirmSetting) {
        return tabs;
      }
      return [
        {
          text: 'Nowe',
          route: { name: 'benefitList', params: { state: BenefitChangeState.NEW } },
          value: 'NEW',
          actions: true
        },
        {
          text: 'Edytowane',
          route: { name: 'benefitList', params: { state: BenefitChangeState.EDITED } },
          value: 'EDITED',
          actions: true
        },
        ...tabs
      ];
    }
  },
  watch: {
    $route(to, from) {
      this.$store.commit('breadcrumbs/set', [
        { text: 'Usługi', to: { name: 'benefits' }, exact: true },
        { text: BenefitChangeStateTranslation[BenefitChangeState[to.params.state]], disabled: true }
      ]);
    }
  }
};
</script>
